import { ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import { DraftTag } from '@/features/menu/components/DraftTag';
import type {
  ChannelDraft,
  ChoiceFlag,
  ProductView,
  SubLine,
} from '@/features/menu/utils/menu-draft';
import { describeHours, describeRules } from '@/features/menu/utils/menu-draft-text';
import { useI18nStore, useT } from '@/i18n';
import type { Translations } from '@/i18n/locales/en';
import { formatMoney } from '@/utils/money';
import { memo, type ReactNode } from 'react';

function Line({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <div className="quick-edit-line" data-testid={testId}>
      {children}
    </div>
  );
}

function Label({ text }: { text: string }) {
  return text ? <span className="quick-edit-line__label">{text}</span> : null;
}

function State({ text }: { text: string }) {
  return <span className="ai-changed-text">{text}</span>;
}

export function flagText(flag: ChoiceFlag, on: boolean, q: Translations['menuQuickEdit']): string {
  switch (flag) {
    case 'inStock':
      return on ? q.inStock : q.soldOut;
    case 'active':
      return on ? q.shown : q.hidden;
    case 'default':
      return on ? q.isDefault : q.notDefault;
  }
}

export function ChannelLines({
  channels,
  currency,
  testId,
}: {
  channels: ChannelDraft[];
  currency: string;
  testId: string;
}) {
  const q = useT().menuQuickEdit;
  const money = (value: number) => formatMoney(value, currency);
  const value = (price: number | null, draft: ChannelDraft) =>
    price === null ? `${money(draft.base)} (${q.basePrice})` : money(price);
  return channels.map((channel) => (
    <Line key={channel.channel} testId={`${testId}-${channel.channel}`}>
      <Label text={`${channel.channel === 'PICKUP' ? q.pickup : q.delivery} `} />
      <ProposedValue
        start
        before={value(channel.before, channel)}
        after={value(channel.after, channel)}
      />
    </Line>
  ));
}

export const ProductDraftLines = memo(function ProductDraftLines({
  view,
  currency,
}: {
  view: ProductView;
  currency: string;
}) {
  const t = useT();
  const q = t.menuQuickEdit;
  const locale = useI18nStore((s) => s.locale);
  const money = (value: number, sign: '' | '+' = '') => `${sign}${formatMoney(value, currency)}`;
  const channelName = (channel: ChannelDraft['channel']) =>
    channel === 'PICKUP' ? q.pickup : q.delivery;
  const channelValue = (value: number | null, draft: ChannelDraft, sign: '' | '+') =>
    value === null ? `${money(draft.base, sign)} (${q.basePrice})` : money(value, sign);
  const id = view.product.id;

  const sub = (line: SubLine): ReactNode => {
    const testId = `menu-quick-edit-sub-${line.key}`;
    switch (line.type) {
      case 'status':
        return (
          <Line key={line.key} testId={testId}>
            <span className={line.status === 'removed' ? 'quick-edit-old' : undefined}>
              {line.label}
            </span>
            {line.rules && <span>{` (${describeRules(line.rules, q)})`}</span>}
            {line.price !== null && <State text={` ${money(line.price, line.sign)}`} />}
            <DraftTag status={line.status} testId={`${testId}-tag`} />
          </Line>
        );
      case 'text':
        return (
          <Line key={line.key} testId={testId}>
            <Label text={line.label ? `${line.label} · ` : ''} />
            <ProposedValue start before={line.before} after={line.after} />
          </Line>
        );
      case 'money':
        return (
          <Line key={line.key} testId={testId}>
            <Label text={`${line.label} `} />
            <ProposedValue
              start
              before={money(line.before, line.sign)}
              after={money(line.after, line.sign)}
            />
          </Line>
        );
      case 'channel':
        return (
          <Line key={line.key} testId={testId}>
            <Label text={`${line.label} · ${channelName(line.draft.channel)} `} />
            <ProposedValue
              start
              before={channelValue(line.draft.before, line.draft, line.sign)}
              after={channelValue(line.draft.after, line.draft, line.sign)}
            />
          </Line>
        );
      case 'rules':
        return (
          <Line key={line.key} testId={testId}>
            <Label text={`${line.label} `} />
            <ProposedValue
              start
              before={describeRules(line.before, q)}
              after={describeRules(line.after, q)}
            />
          </Line>
        );
      case 'flag':
        return (
          <Line key={line.key} testId={testId}>
            <Label text={`${line.label} `} />
            <State text={flagText(line.flag, line.after, q)} />
          </Line>
        );
      case 'order':
        return (
          <Line key={line.key} testId={testId}>
            <Label text={`${line.label || q.order}: `} />
            <ProposedValue start before={line.before.join(', ')} after={line.after.join(', ')} />
          </Line>
        );
    }
  };

  const { description, hours, channels, subs } = view;
  if (!description && !hours && channels.length === 0 && subs.length === 0) return null;
  return (
    <div className="quick-edit-detail-lines">
      {description && (
        <Line testId={`menu-quick-edit-description-${id}`}>
          <ProposedValue start before={description.before} after={description.after} />
        </Line>
      )}
      {hours && (
        <Line testId={`menu-quick-edit-hours-${id}`}>
          <Label text={`${q.hours} `} />
          <ProposedValue
            start
            before={describeHours(hours.before, locale, q)}
            after={describeHours(hours.after, locale, q)}
          />
        </Line>
      )}
      <ChannelLines
        channels={channels}
        currency={currency}
        testId={`menu-quick-edit-channel-${id}`}
      />
      {subs.map(sub)}
    </div>
  );
});
